/* eslint-disable react-refresh/only-export-components */
/**
 * Auth-провайдер и хук `useAuth` (Фаза 3, И3).
 *
 * Состояние сессии живёт в httpOnly cookie — фронт её прочитать не может, поэтому
 * при старте приложения провайдер дёргает `GET /api/auth/me` (bootstrap), чтобы
 * узнать, залогинен ли текущий браузер. Пока идёт bootstrap — `loading=true`
 * (гейт защищённых роутов ждёт его завершения, чтобы не мигать редиректом).
 *
 * `signIn`/`signUp`/`signOut` вызывают auth-клиент ([`auth.ts`](./auth.ts)) и
 * обновляют локальное `user`; после успешных `signIn`/`signUp` cookie уже выставлен
 * сервером, состояние синхронно.
 *
 * Файл экспортирует и компонент (`AuthProvider`), и хук (`useAuth`) — поэтому
 * `react-refresh/only-export-components` локально отключён (контекст + провайдер +
 * хук — стандартный паттерн React).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchMe,
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
  resendVerification as apiResendVerification,
  verifyEmail as apiVerifyEmail,
  type AuthUser,
  type RegisterBody,
  type VerifyStatus,
} from './auth';
import type { CaptchaSolution } from './api';

export type { AuthUser } from './auth';

/**
 * Параметры `signUp` — расширенное тело регистрации (И3): `username` обяз.,
 * `fullName?` опц., `consent` обяз., плюс скрытые anti-spam-поля (`website`,
 * `ts`). Передаётся 1:1 в [`register`](./auth.ts) как `RegisterBody`.
 */
export type SignUpInput = RegisterBody;
export type { VerifyStatus } from './auth';

/** Состояние аутентификации, доступное дочерним экранам через `useAuth()`. */
export interface AuthState {
  /** Текущий пользователь или `null` (аноним). */
  user: AuthUser | null;
  /** `true`, пока идёт bootstrap `/me` при старте (первый рендер ещё не знает). */
  loading: boolean;
  /** Вход: при успехе обновляет `user` и возвращает его. captcha — опц. решение
   *  слайд-капчи (И8): передаётся при адаптивном требовании сервера (captcha_required). */
  signIn: (email: string, password: string, captcha?: CaptchaSolution) => Promise<AuthUser>;
  /** Регистрация: при успехе (201) обновляет `user` (verified:false до /verify). */
  signUp: (input: SignUpInput) => Promise<AuthUser>;
  /** Выход: при успехе обнуляет `user`. */
  signOut: () => Promise<void>;
  /**
   * Подтверждение email по одноразовому токену из письма (`POST /verify`).
   * При `status:"ok"` проставляет текущему `user.verified=true` (если залогинен).
   */
  verifyEmail: (token: string) => Promise<VerifyStatus>;
  /** Повторная отправка письма подтверждения (`POST /resend-verification`). */
  resendVerification: () => Promise<VerifyStatus>;
}

const AuthContext = createContext<AuthState | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** Инъекция `fetch` для тестов (по умолчанию — глобальный `fetch`). */
  fetchImpl?: typeof fetch;
}

export function AuthProvider({ children, fetchImpl }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: определяем залогиненность по cookie при монтировании. 401 — ожидаемо
  // (аноним); любая иная ошибка (сеть) тоже трактуется как «аноним», чтобы UI не
  // зависал в loading — повторный вход восстановит сессию.
  useEffect(() => {
    let cancelled = false;
    fetchMe(fetchImpl)
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchImpl]);

  const signIn = useCallback(
    async (email: string, password: string, captcha?: CaptchaSolution): Promise<AuthUser> => {
      const u = await apiLogin(email, password, captcha, fetchImpl);
      setUser(u);
      return u;
    },
    [fetchImpl],
  );

  const signUp = useCallback(
    async (input: SignUpInput): Promise<AuthUser> => {
      const u = await apiRegister(input, fetchImpl);
      setUser(u);
      return u;
    },
    [fetchImpl],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await apiLogout(fetchImpl);
    setUser(null);
  }, [fetchImpl]);

  const verifyEmail = useCallback(
    async (token: string): Promise<VerifyStatus> => {
      const res = await apiVerifyEmail(token, fetchImpl);
      // Подтверждение может прийти по ссылке из письма даже без активной сессии
      // (публичный эндпоинт): обновляем user.verified только если залогинены.
      if (res.status === 'ok') {
        setUser((prev) => (prev ? { ...prev, verified: true } : prev));
      }
      return res;
    },
    [fetchImpl],
  );

  const resendVerification = useCallback(
    async (): Promise<VerifyStatus> => apiResendVerification(fetchImpl),
    [fetchImpl],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      signIn,
      signUp,
      signOut,
      verifyEmail,
      resendVerification,
    }),
    [user, loading, signIn, signUp, signOut, verifyEmail, resendVerification],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Доступ к auth-состоянию. Бросает, если использован вне `<AuthProvider>`. */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
